import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import {
  api,
  type Game,
  type PitchCount,
  type GameSubmission,
  type InningWithPitches,
} from '../services/api';

@customElement('game-submission-flow')
export class GameSubmissionFlow extends LitElement {
  createRenderRoot() {
    return this;
  }

  @property()
  gameId = '';

  @state()
  private game: Game | null = null;

  @state()
  private pitches: PitchCount[] = [];

  @state()
  private submissions: GameSubmission[] = [];

  @state()
  private innings: InningWithPitches[] = [];

  @state()
  private submitting = false;

  @state()
  private loading = true;

  @state()
  private error = '';

  async connectedCallback() {
    super.connectedCallback();
    await this.loadGameData();
  }

  private async loadGameData() {
    if (!this.gameId) return;

    this.loading = true;
    this.error = '';
    try {
      const [game, pitches, submissions, innings] = await Promise.all([
        api.getGame(this.gameId),
        api.getGamePitches(this.gameId),
        api.getGameSubmissions(this.gameId).catch(() => []),
        api.getGameInnings(this.gameId).catch(() => []),
      ]);
      this.game = game;
      this.pitches = pitches || [];
      this.submissions = submissions || [];
      this.innings = innings || [];
    } catch (err) {
      this.error = err instanceof Error ? err.message : 'Failed to load game';
    } finally {
      this.loading = false;
    }
  }

  private async submitForApproval() {
    this.submitting = true;
    this.error = '';
    try {
      const submission = await api.submitGame(this.gameId);
      this.submissions = [submission, ...this.submissions];
      this.dispatchEvent(
        new CustomEvent('game-submitted', { detail: submission })
      );
    } catch (err) {
      this.error = err instanceof Error ? err.message : 'Submission failed';
    } finally {
      this.submitting = false;
    }
  }

  render() {
    if (this.loading) {
      return html`
        <div class="flex justify-center py-8">
          <span class="loading loading-spinner loading-lg"></span>
        </div>
      `;
    }

    if (this.error) {
      return html`
        <div class="alert alert-error">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="stroke-current shrink-0 h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span>${this.error}</span>
          <button class="btn btn-sm" @click=${this.loadGameData}>Retry</button>
        </div>
      `;
    }

    const latestSubmission = this.submissions[0];
    const canSubmit =
      !latestSubmission || latestSubmission.status === 'rejected' || latestSubmission.status === 'changes_requested';
    const totalPitches = this.pitches.reduce((sum, p) => sum + p.count, 0);
    const playersWithPitches = this.pitches.filter((p) => p.count > 0);

    return html`
      <div class="space-y-4">
        <!-- Game summary -->
        <div class="card bg-base-100 shadow">
          <div class="card-body">
            <h3 class="card-title">${this.game?.name}</h3>
            <div class="text-sm text-base-content/70">
              ${this.game?.game_date
                ? new Date(this.game.game_date).toLocaleDateString()
                : ''}
              ${this.game?.location ? ` - ${this.game.location}` : ''}
            </div>

            ${playersWithPitches.length > 0
              ? html`
                  <!-- Pitch count summary table -->
                  <table class="table table-sm mt-4">
                    <thead>
                      <tr>
                        <th>Player</th>
                        <th class="text-right">Pitches</th>
                      </tr>
                    </thead>
                    <tbody>
                      ${playersWithPitches.map(
                        (p) => html`
                          <tr>
                            <td>
                              ${p.player_name}
                              ${p.jersey_number
                                ? html`<span class="badge badge-ghost ml-2"
                                    >#${p.jersey_number}</span
                                  >`
                                : nothing}
                            </td>
                            <td class="text-right font-mono">${p.count}</td>
                          </tr>
                        `
                      )}
                    </tbody>
                    <tfoot>
                      <tr class="font-bold">
                        <td>Total</td>
                        <td class="text-right font-mono">${totalPitches}</td>
                      </tr>
                    </tfoot>
                  </table>
                `
              : html`
                  <div class="alert alert-warning mt-4">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      class="stroke-current shrink-0 h-6 w-6"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                      />
                    </svg>
                    <span>No pitch counts recorded for this game.</span>
                  </div>
                `}
          </div>
        </div>

        <!-- Per-inning breakdown -->
        ${this.innings.length > 0 && playersWithPitches.length > 0
          ? this.renderInningBreakdown(playersWithPitches)
          : nothing}

        <!-- Submission status -->
        ${latestSubmission ? this.renderSubmissionStatus(latestSubmission) : nothing}

        <!-- Submission history -->
        ${this.submissions.length > 1
          ? html`
              <div class="collapse collapse-arrow bg-base-100 shadow">
                <input type="checkbox" />
                <div class="collapse-title font-medium">
                  Submission History (${this.submissions.length})
                </div>
                <div class="collapse-content">
                  <div class="space-y-2">
                    ${this.submissions.map((sub) => this.renderSubmissionHistoryItem(sub))}
                  </div>
                </div>
              </div>
            `
          : nothing}

        <!-- Submit button -->
        ${canSubmit
          ? html`
              <button
                class="btn btn-primary w-full"
                @click=${this.submitForApproval}
                ?disabled=${this.submitting || playersWithPitches.length === 0}
              >
                ${this.submitting
                  ? html`<span class="loading loading-spinner"></span>
                      Submitting...`
                  : 'Submit for Approval'}
              </button>
            `
          : nothing}
      </div>
    `;
  }

  private formatInningLabel(inning: InningWithPitches): string {
    const half = inning.half === 'top' ? '▲' : '▼';
    return `${inning.inning_number}${half}`;
  }

  private renderInningBreakdown(playersWithPitches: PitchCount[]) {
    // Build a map of player_id -> { name, jersey, innings: { inning_id -> count } }
    const playerMap = new Map<string, { name: string; jersey: number | null; innings: Map<string, number> }>();

    for (const p of playersWithPitches) {
      if (!playerMap.has(p.player_id)) {
        playerMap.set(p.player_id, {
          name: p.player_name || 'Unknown',
          jersey: p.jersey_number ?? null,
          innings: new Map(),
        });
      }
    }

    // Fill per-inning pitch counts from innings data
    for (const inning of this.innings) {
      for (const ip of inning.pitches) {
        const player = playerMap.get(ip.player_id);
        if (player) {
          player.innings.set(inning.id, (player.innings.get(inning.id) || 0) + ip.pitch_count);
        }
      }
    }

    return html`
      <div class="card bg-base-100 shadow">
        <div class="card-body">
          <h4 class="font-semibold text-sm">Per-Inning Breakdown</h4>
          <div class="overflow-x-auto">
            <table class="table table-xs">
              <thead>
                <tr>
                  <th>Player</th>
                  ${this.innings.map(
                    (inn) => html`<th class="text-center tabular-nums">${this.formatInningLabel(inn)}</th>`
                  )}
                  <th class="text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                ${[...playerMap.entries()].map(
                  ([playerId, player]) => {
                    const total = playersWithPitches.find((p) => p.player_id === playerId)?.count ?? 0;
                    return html`
                      <tr>
                        <td class="whitespace-nowrap">
                          ${player.jersey != null
                            ? html`<span class="font-bold text-primary mr-1">#${player.jersey}</span>`
                            : nothing}
                          ${player.name}
                        </td>
                        ${this.innings.map(
                          (inn) => {
                            const count = player.innings.get(inn.id) || 0;
                            return html`<td class="text-center tabular-nums ${count > 0 ? '' : 'text-base-content/30'}">${count || '-'}</td>`;
                          }
                        )}
                        <td class="text-right font-bold tabular-nums">${total}</td>
                      </tr>
                    `;
                  }
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    `;
  }

  private renderSubmissionStatus(submission: GameSubmission) {
    const statusConfig: Record<string, { badge: string; icon: string; title: string; message: string }> = {
      pending: {
        badge: 'badge-warning',
        icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
        title: 'Pending Approval',
        message: 'This game is awaiting review by an administrator.',
      },
      pending_away: {
        badge: 'badge-warning',
        icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
        title: 'Awaiting Opposing Coach',
        message: 'This game is awaiting review by the opposing team\'s coach.',
      },
      pending_admin: {
        badge: 'badge-warning',
        icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
        title: 'Awaiting Admin Review',
        message: 'The opposing team has no registered coach. An administrator will review.',
      },
      approved: {
        badge: 'badge-success',
        icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
        title: 'Approved',
        message: 'This game has been approved.',
      },
      changes_requested: {
        badge: 'badge-error',
        icon: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z',
        title: 'Changes Requested',
        message: submission.change_request_message || 'The opposing coach has requested changes.',
      },
      rejected: {
        badge: 'badge-error',
        icon: 'M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z',
        title: 'Rejected',
        message: submission.rejection_reason || 'This game was rejected.',
      },
    };

    const config = statusConfig[submission.status];

    return html`
      <div
        class="alert ${submission.status === 'approved'
          ? 'alert-success'
          : submission.status === 'rejected' || submission.status === 'changes_requested'
            ? 'alert-error'
            : 'alert-warning'}"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="stroke-current shrink-0 h-6 w-6"
          fill="none"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="${config.icon}"
          />
        </svg>
        <div>
          <h4 class="font-bold">${config.title}</h4>
          <p class="text-sm">${config.message}</p>
          ${submission.reviewed_by_name
            ? html`
                <p class="text-xs mt-1">
                  Reviewed by ${submission.reviewed_by_name}
                  ${submission.reviewed_at
                    ? ` on ${new Date(submission.reviewed_at).toLocaleDateString()}`
                    : ''}
                </p>
              `
            : html`
                <p class="text-xs mt-1">
                  Submitted by ${submission.submitted_by_name || 'Unknown'}
                  on ${new Date(submission.submitted_at).toLocaleDateString()}
                </p>
              `}
        </div>
      </div>
    `;
  }

  private renderSubmissionHistoryItem(submission: GameSubmission) {
    const statusBadge: Record<string, string> = {
      pending: 'badge-warning',
      pending_away: 'badge-warning',
      pending_admin: 'badge-warning',
      approved: 'badge-success',
      rejected: 'badge-error',
      changes_requested: 'badge-error',
    };

    return html`
      <div class="flex items-center justify-between p-2 bg-base-200 rounded">
        <div>
          <span class="badge ${statusBadge[submission.status]}">
            ${submission.status}
          </span>
          <span class="text-sm ml-2">
            ${new Date(submission.submitted_at).toLocaleString()}
          </span>
        </div>
        ${submission.rejection_reason
          ? html`<span class="text-sm text-error"
              >${submission.rejection_reason}</span
            >`
          : nothing}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'game-submission-flow': GameSubmissionFlow;
  }
}
